import React, { useState, useRef } from 'react';
import { Upload, FileSpreadsheet, CheckCircle, Package, Info, Loader2, XCircleIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useUIState } from '@/core/UIStateProvider';
import { useCreateProject, useProcessFile, useUploadFile } from '@/core/api/queries';

const ProjectOnboarding: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { addNotification } = useUIState();
  const createProject = useCreateProject();
  const uploadFile = useUploadFile();
  const processFile = useProcessFile();

  const supportedFormats = [
    {
      name: 'CSV',
      extension: '.csv',
      icon: FileSpreadsheet,
      color: 'text-green-600',
    },
    {
      name: 'Excel',
      extension: '.xlsx, .xls',
      icon: FileSpreadsheet,
      color: 'text-blue-600',
    },
  ];

  const handleFileSelect = (file: File) => {
    // Validate file type
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/json', // .json
    ];
    const allowedExtensions = ['.csv', '.xlsx', '.xls', '.json'];
    const fileExtension = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

    if (allowedTypes.includes(file.type) || allowedExtensions.includes(fileExtension)) {
      setSelectedFile(file);
    } else {
      addNotification('error', 'Please select a valid file format (CSV, Excel, or JSON)');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleClearFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;

    setIsUploading(true);

    try {
      // Step 1: Create project
      setUploadStatus('Creating your project...');
      const projectName = selectedFile.name.replace(/\.[^/.]+$/, '');

      const project = await createProject.mutateAsync({
        name: projectName,
        description: `Project created from ${selectedFile.name}`,
      });

      // Step 2: Upload file
      setUploadStatus('Uploading file...');
      const { fileRecord } = await uploadFile.mutateAsync({
        projectId: project.id,
        file: selectedFile,
      });

      // Step 3: Process file and extract shipments
      setUploadStatus('Processing file and extracting shipments...');
      await processFile.mutateAsync({
        fileId: fileRecord.id,
        data: {},
      });

      addNotification('success', 'Table created and shipments extracted successfully! Start processing shipments...');
      setTimeout(() => navigate('/app/table'), 2000);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to create table. Please try again.';
      addNotification('error', errorMessage);
      setIsUploading(false);
      setUploadStatus('');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Main Content Container */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        {/* Welcome Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-6 shadow-lg">
            <Package className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to Your Freight Management Dashboard</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">Get started by uploading your first shipment file.</p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden mb-8">
          {/* Info Section */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-6 border-b border-gray-100">
            <div className="flex items-start space-x-3">
              <Info className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">How It Works</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Upload a file containing your shipments data</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>We'll automatically extract and organize your shipments</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Your table will be created and ready to use</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Upload Section */}
          <div className="p-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">Upload Your Shipment File</h2>

            {/* Drag & Drop Zone */}
            <div
              onDragOver={!isUploading ? handleDragOver : undefined}
              onDragLeave={!isUploading ? handleDragLeave : undefined}
              onDrop={!isUploading ? handleDrop : undefined}
              className={`
                relative border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200
                ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-gray-50'}
                ${selectedFile ? 'bg-green-50 border-green-300' : ''}
                ${isUploading ? 'opacity-75 cursor-not-allowed' : ''}
              `}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,.xlsx,.xls,.json"
                onChange={handleFileInputChange}
                disabled={isUploading}
                className="hidden"
              />

              {!selectedFile ? (
                <>
                  <div className="mb-6">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-white rounded-full shadow-md mb-4">
                      <Upload className="w-10 h-10 text-gray-400" />
                    </div>
                    <p className="text-lg font-medium text-gray-900 mb-2">Drag and drop your file here</p>
                    <p className="text-gray-500 mb-4">or click to browse from your computer</p>
                    <p className="text-gray-500 mb-6 text-sm">Supported file formats: CSV, Excel</p>
                  </div>

                  <button
                    onClick={handleUploadClick}
                    disabled={isUploading}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-3 rounded-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    Choose File
                  </button>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-2">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileSpreadsheet className="w-8 h-8 text-green-600" />
                        <div className="text-left">
                          <p className="font-medium text-gray-900">{selectedFile.name}</p>
                          <p className="text-sm text-gray-500">{formatFileSize(selectedFile.size)}</p>
                        </div>
                      </div>
                      <button
                        onClick={handleClearFile}
                        disabled={isUploading}
                        className="text-gray-400 hover:text-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <XCircleIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {uploadStatus && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-center justify-center space-x-3">
                          <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                          <span className="text-blue-800 font-medium">{uploadStatus}</span>
                        </div>
                      </div>
                    )}
                    <div className="flex space-x-4 justify-center mt-6">
                      <button
                        onClick={handleClearFile}
                        disabled={isUploading}
                        className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Choose Different File
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={isUploading}
                        className="px-8 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-medium transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                      >
                        {isUploading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>Processing...</span>
                          </>
                        ) : (
                          <span>Create</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Supported Formats Section */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6 text-center">Supported File Formats</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {supportedFormats.map((format) => {
              const IconComponent = format.icon;
              return (
                <div
                  key={format.name}
                  className="flex items-center space-x-4 p-6 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors border border-gray-200"
                >
                  <div className="flex-shrink-0">
                    <IconComponent className={`w-10 h-10 ${format.color}`} />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 text-lg">{format.name}</h4>
                    <p className="text-sm text-gray-500">{format.extension}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Additional Info */}
          <div className="mt-8 p-6 bg-blue-50 rounded-xl border border-blue-100">
            <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
              <Info className="w-5 h-5 text-blue-600 mr-2" />
              File Requirements
            </h4>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start">
                <span className="text-blue-600 mr-2">•</span>
                <span>
                  Ideally, the file should contain columns for: Container or Booking Number, Origin, Destination, Dates
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOnboarding;
